/**
 * mediaUpload.js — browser-side helpers shared by the Media Library uploader and
 * the Media Intake staging area.
 *
 * These originally lived inside MediaLibrary.js. They were lifted out when Media
 * Intake needed the identical behaviour: two copies of "how we resize, hash and
 * poster a file before upload" is precisely the kind of mirror that drifts and
 * then produces a bug nobody can reproduce on the other screen.
 *
 * All pure browser functions — no network, no React.
 */

export const ORIGINAL_MAX_EDGE = 2048;
export const THUMB_MAX_EDGE = 480;
export const ORIGINAL_QUALITY = 0.88;
export const THUMB_QUALITY = 0.78;

export const MEDIA_EXT_RE = /\.(jpe?g|png|webp|gif|heic|heif|mp4|mov|m4v|webm|ogg)$/i;
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|ogg)$/i;
const HEIC_EXT_RE = /\.(heic|heif)$/i;

export const isVideoFile = (f) => VIDEO_EXT_RE.test(f.name) || /^video\//.test(f.type || '');
export const isHeicFile = (f) => HEIC_EXT_RE.test(f.name) || /^image\/hei[cf]$/.test(f.type || '');
export const isMediaFile = (f) => MEDIA_EXT_RE.test(f.name) || /^(image|video)\//.test(f.type || '');
export const isGifFile = (f) => /\.gif$/i.test(f?.name || '') || (f?.type || '') === 'image/gif';

// A GIF at or under this ships as RAW bytes (animation + transparency survive);
// base64 of 4.5MB stays under Netlify's ~6MB function-body cap. Bigger GIFs
// fall back to the canvas path (flattened to one JPEG frame — the old behavior).
export const GIF_RAW_MAX_BYTES = 4_500_000;

/** Decode just for dimensions — no canvas re-encode (used by the raw-GIF path). */
export async function fileImageDims(file) {
  try {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('undecodable'));
        el.src = url;
      });
      return { width: img.naturalWidth || null, height: img.naturalHeight || null };
    } finally { URL.revokeObjectURL(url); }
  } catch (_) { return { width: null, height: null }; }
}

/** Kebab-case slug used for every tag we write, client and server side. */
export const normalizeTag = (s) =>
  String(s || '').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

/** Resize in a canvas and return base64 JPEG plus the file's true dimensions. */
export async function fileToResizedBase64(file, maxEdge, quality) {
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('Could not read file'));
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Could not decode image (HEIC and RAW formats are not supported — convert to JPEG/PNG first)'));
    el.src = dataUrl;
  });
  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  const scale = Math.min(1, maxEdge / Math.max(naturalWidth, naturalHeight));
  const w = Math.max(1, Math.round(naturalWidth * scale));
  const h = Math.max(1, Math.round(naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  const jpeg = canvas.toDataURL('image/jpeg', quality);
  return { base64: jpeg.slice(jpeg.indexOf(',') + 1), naturalWidth, naturalHeight };
}

/**
 * Alpha-aware original export for uploads. The plain resize helper above
 * flattens EVERYTHING to JPEG, which silently destroys transparency — a brand
 * logo on a transparent background comes out baked onto black (this is why the
 * library's old Delivered-logo PNGs are black-backed and unusable). This
 * variant checks the decoded pixels for real transparency (PNG/WebP only; a
 * .png that's actually an opaque screenshot still ships as JPEG) and exports
 * transparent files as PNG instead, stepping the max edge down if the PNG
 * base64 would blow past Netlify's ~6MB function body cap.
 * Returns { base64, naturalWidth, naturalHeight, format: 'jpeg'|'png', hasTransparency }.
 */
export async function fileToUploadOriginal(file, maxEdge = ORIGINAL_MAX_EDGE, quality = ORIGINAL_QUALITY) {
  const alphaCapable = /\.(png|webp)$/i.test(file.name || '') || /^image\/(png|webp)$/.test(file.type || '');
  if (!alphaCapable) {
    const r = await fileToResizedBase64(file, maxEdge, quality);
    return { ...r, format: 'jpeg', hasTransparency: false };
  }
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('Could not read file'));
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Could not decode image (HEIC and RAW formats are not supported — convert to JPEG/PNG first)'));
    el.src = dataUrl;
  });
  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  const draw = (edge) => {
    const scale = Math.min(1, edge / Math.max(naturalWidth, naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(naturalHeight * scale));
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  };
  // Probe alpha on a small render — presence of transparency survives scaling,
  // and getImageData over a 512px canvas is cheap where 2048px is not.
  const probe = draw(Math.min(512, Math.max(naturalWidth, naturalHeight)));
  const px = probe.getContext('2d').getImageData(0, 0, probe.width, probe.height).data;
  let transparent = false;
  for (let i = 3; i < px.length; i += 4) { if (px[i] < 250) { transparent = true; break; } }
  if (!transparent) {
    const canvas = draw(maxEdge);
    const jpeg = canvas.toDataURL('image/jpeg', quality);
    return { base64: jpeg.slice(jpeg.indexOf(',') + 1), naturalWidth, naturalHeight, format: 'jpeg', hasTransparency: false };
  }
  // Transparent — must stay PNG. PNG is heavier than JPEG, so step the edge
  // down until the payload fits the function cap (logos fit at full size;
  // only huge photographic RGBA files ever step down).
  let base64 = '';
  for (const edge of [maxEdge, 1600, 1200, 900]) {
    const png = draw(edge).toDataURL('image/png');
    base64 = png.slice(png.indexOf(',') + 1);
    if (base64.length <= 5_200_000) break;
  }
  return { base64, naturalWidth, naturalHeight, format: 'png', hasTransparency: true };
}

/**
 * Read raw bytes as base64 with NO canvas step. Required for HEIC: the browser
 * cannot decode it, so the server does the conversion instead.
 */
export async function fileToRawBase64(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('Could not read file'));
    fr.readAsDataURL(file);
  });
  return String(dataUrl).slice(String(dataUrl).indexOf(',') + 1);
}

export async function sha256Hex(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Grab a poster frame, duration and dimensions from a video, entirely in the
 * browser. Seeks a little past the start so we don't capture a black opening
 * frame. Throws on codecs the browser can't decode (iPhone HEVC) — callers are
 * expected to fall back to a placeholder rather than failing the upload.
 */
export async function videoPoster(file) {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.muted = true; video.playsInline = true; video.preload = 'metadata'; video.src = url;
    await new Promise((resolve, reject) => {
      video.onloadeddata = resolve;
      video.onerror = () => reject(new Error('Could not read video (unsupported codec — try MP4/H.264)'));
      setTimeout(() => reject(new Error('Timed out reading video')), 12000);
    });
    const target = Math.min(1, (video.duration || 2) / 3);
    // ⚠ THE SEEK MUST BE TIME-BOUNDED. A phone .MOV whose container parses
    // but whose codec the browser cannot decode (HEVC/H.265, ProRes) fires
    // onloadeddata and then never fires onseeked, so an un-raced promise here
    // NEVER SETTLES. runUploads processes files sequentially, so that one file
    // hangs on "reading video..." and silently takes every remaining file in
    // the batch with it - which reads as "the upload just stopped".
    // Resolve rather than reject: a missing poster is fine, the server stores a
    // placeholder thumbnail and classifies from the filename.
    await Promise.race([
      new Promise((resolve) => { video.onseeked = resolve; try { video.currentTime = target; } catch { resolve(); } }),
      new Promise((resolve) => setTimeout(resolve, 8000)),
    ]);
    const w = video.videoWidth, h = video.videoHeight;
    // No decoded frame means no poster. Drawing a 0x0 (or never-decoded) video
    // yields a black or empty tile that looks like a broken asset in the grid.
    if (!w || !h) {
      return {
        posterBase64: null,
        duration: isFinite(video.duration) ? Math.round(video.duration) : null,
        width: null, height: null,
      };
    }
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    const jpeg = canvas.toDataURL('image/jpeg', 0.82);
    return {
      posterBase64: jpeg.slice(jpeg.indexOf(',') + 1),
      duration: isFinite(video.duration) ? Math.round(video.duration) : null,
      width: w, height: h,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** r2-image proxies bytes out of the private bucket. */
export function thumbUrl(key) {
  return key ? `/.netlify/functions/r2-image?key=${encodeURIComponent(key)}` : '';
}
